// The account a member picked for a FAST gift, kept across a WebView reload.
//
// The FAST page charges the eGIRO mandate chosen on giving-options, held in the
// myGiving store, which is not persisted. When the WebView reloads on that page
// (iOS reclaiming it in the background, an OTA update) the store comes back
// empty. This keeps just enough to restore the choice: which mandate, for which
// fund, by whom and when. The account's bank, masked number and limit are not
// kept; they are re-read from the member's freshly loaded linked accounts, and an
// account that is no longer linked (removed, or retired by giving-api) is not
// restored. sessionStorage lasts across reloads but not across app launches, so a
// stale choice cannot come back later.

const KEY = 'mygiving:fast-selection';
// Long enough to pick up after a reload, short enough not to resurface later.
export const FAST_SELECTION_TTL_MS = 30 * 60 * 1000;

export function saveFastSelection(
  { memberId, eGiroMandateId, giveType, isFromGivenNow },
  now = Date.now(),
) {
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({
        memberId,
        eGiroMandateId,
        giveType,
        isFromGivenNow: !!isFromGivenNow,
        savedAt: now,
      }),
    );
  } catch {
    // Storage unavailable (private mode, quota): the page then asks again.
  }
}

/** The saved choice for this member, or null when none, expired, or malformed. */
export function loadFastSelection(memberId, now = Date.now()) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    if (!saved || typeof saved.eGiroMandateId !== 'string' || !saved.eGiroMandateId) return null;
    if (saved.memberId !== memberId) return null;
    if (!(now - saved.savedAt >= 0 && now - saved.savedAt <= FAST_SELECTION_TTL_MS)) return null;
    return saved;
  } catch {
    return null;
  }
}

export function clearFastSelection() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}

/** The saved mandate's account if it is still linked, else null. */
export function findRestorableAccount(saved, linkedAccounts) {
  if (!saved || !Array.isArray(linkedAccounts)) return null;
  return (
    linkedAccounts.find(
      account => account.eGiroMandateId === saved.eGiroMandateId && account.state === 'Linked',
    ) || null
  );
}
